import Html from 'slate-html-serializer';
import { Editor, getEventTransfer } from 'slate-react';
import { Value } from 'slate';
import React from 'react';
import { RULES, R_ALIGNED_BLOCK } from './serializer-rules';
import PropTypes from 'prop-types';
import { isKeyHotkey } from 'is-hotkey';

import { IconButton, SHAPES } from '@projeccions-pdc/react-icon';
import './text-editors.css';
import Popover from 'react-popover';

import isUrl from 'is-url';

const toolbarButtonPrimary = '#fff';
const toolbarButtonSecondary = '#777';

const DEFAULT_NODE = 'paragraph';
let initialValue = {
    document: {
        nodes: [
            {
                object: 'block',
                type: 'paragraph',
                nodes: [],
            },
        ],
    },
};

const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');

/**
 * Create a new HTML serializer with `RULES`.
 *
 * @type {Html}
 */

const serializer = new Html({ rules: RULES });

/**
 * The pasting html example.
 *
 * @type {Component}
 */

export class RichText extends React.Component {
    static propTypes = {
        slot: PropTypes.string.isRequired,
        placeholder: PropTypes.string.isRequired,
        content: PropTypes.string,
        simpleMode: PropTypes.bool,
        noHeading: PropTypes.bool,
        className: PropTypes.string,
        style: PropTypes.object,

        onChange: PropTypes.func.isRequired,
        modalPrompt: PropTypes.func,
        onFocused: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = this.readState(props);
        this.editorRef = React.createRef();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.content !== state.oldcontent) {
            let newState = {
                menuVisible: false,
                oldcontent: props.content,
            };
            try {
                let initialContent = props.content;
                if (initialContent && initialContent.length > 0) {
                    if (initialContent[0].trim() !== '<') {
                        // Init with harmless tag
                        initialContent = `<span>${props.content}</span>`;
                    }
                    newState.value = serializer.deserialize(initialContent);
                } else {
                    newState.value = Value.fromJSON(initialValue);
                }
            } catch (err) {
                newState.value = Value.fromJSON(initialValue);
            }
            return newState;
        }
        return null;
    }

    readState(props) {
        let state = {
            menuVisible: false,
            oldcontent: props.content,
        };
        try {
            let initialContent = props.content;
            if (initialContent && initialContent.length > 0) {
                if (initialContent[0].trim() !== '<') {
                    // Init with harmless tag
                    initialContent = `<span>${props.content}</span>`;
                }
                state.value = serializer.deserialize(initialContent);
            } else {
                state.value = Value.fromJSON(initialValue);
            }
        } catch (err) {
            state.value = Value.fromJSON(initialValue);
        }
        // console.log('state', state)
        return state;
    }

    /**
     * On change, save the new value.
     *
     * @param {Change} change
     */

    onChange = ({ value }) => {
        this.setState({ value });
    };

    /**
     * On paste, deserialize the HTML and then insert the fragment.
     *
     * @param {Event} event
     * @param {Change} change
     */

    onPaste = (event, change) => {
        const transfer = getEventTransfer(event);
        if (transfer.type !== 'html') return;
        const { document } = serializer.deserialize(transfer.html);
        change.insertFragment(document);
        return true;
    };

    onFocus = (state) => {
        setTimeout(() => {
            this.setState({ menuVisible: true });
            if (this.props.onFocused) {
                this.props.onFocused(true);
            }
        }, 100);
    };

    onBlur = (state) => {
        this.setState({ menuVisible: false });
        this.props.onChange(this.props.slot, serializer.serialize(this.state.value));
        if (this.props.onFocused) {
            this.props.onFocused(false);
        }
    };

    /**
     * Check if the current selection has a mark with `type` in it.
     *
     * @param {String} type
     * @return {Boolean}
     */
    hasMark = (type) => {
        const { value } = this.state;
        if (!value) return false;
        return value.activeMarks.some((mark) => mark.type === type);
    };

    /**
     * Check if the current selection has a mark links in it.
     *
     * @return {Boolean}
     */

    hasLink = () => {
        const { value } = this.state;
        return value.inlines.some((inline) => inline.type === 'link');
    };

    /**
     * Check if the any of the currently selected blocks are of `type`.
     *
     * @param {String} type
     * @return {Boolean}
     */

    hasBlock = (type) => {
        const { value } = this.state;
        return value.blocks.some((node) => node.type === type);
    };

    /**
     * On key down, if it's a formatting command toggle a mark.
     *
     * @param {Event} event
     * @param {Change} change
     * @return {Change}
     */

    onKeyDown = (event, change) => {
        let mark;

        if (isBoldHotkey(event)) {
            mark = 'bold';
        } else if (isItalicHotkey(event)) {
            mark = 'italic';
        } else if (isUnderlinedHotkey(event)) {
            mark = 'underline';
        } else {
            return;
        }

        event.preventDefault();
        change.toggleMark(mark);
        return true;
    };

    /**
     * When a mark button is clicked, toggle the current mark.
     *
     * @param {Event} event
     * @param {String} type
     */

    onClickMark = (event, type) => {
        event.preventDefault();
        const { value } = this.state;
        const change = value.change().toggleMark(type);
        this.onChange(change);
    };

    /**
     * When a link button is clicked ask for the url
     *
     * @param {Event} event
     */
    onClickLink = async (event) => {
        event.preventDefault();
        const { value } = this.state;
        const hasLink = this.hasLink();
        const change = value.change();

        if (hasLink) {
            change.unwrapInline('link');
        } else if (value.isExpanded) {
            let href = this.props.modalPrompt
                ? await this.props.modalPrompt('Enter URL:', 'http://')
                : window.prompt('Enter URL:', 'http://');
            href = href ? href.trim().toLocaleLowerCase() : undefined;

            if (isUrl(href) || href.indexOf('mailto:') === 0) {
                change.call((change, href) => {
                    change.wrapInline({
                        type: 'link',
                        data: { href },
                    });

                    change.collapseToEnd();
                }, href);
            }
        } else {
            let href = this.props.modalPrompt
                ? await this.props.modalPrompt('Enter URL:', 'http://')
                : window.prompt('Enter URL:', 'http://');
            href = href ? href.trim().toLocaleLowerCase() : undefined;
            if (isUrl(href) || href.startsWith('#') || href.indexOf('mailto:') === 0) {
                const text = this.props.modalPrompt ? await this.props.modalPrompt('Enter link text:') : window.prompt('Enter link text:');
                if (text) {
                    change
                        .insertText(text)
                        .moveFocusBackward(text.length)
                        //.extend(0 - text.length)
                        .call((change, href) => {
                            change.wrapInline({
                                type: 'link',
                                data: { href },
                            });

                            //change.collapseToEnd();
                            change.moveToEnd();
                        }, href);
                }
            }
        }
        this.onChange(change);
    };

    /**
     * When a block button is clicked, toggle the block type.
     *
     * @param {Event} event
     * @param {String} type
     */

    onClickBlock = (event, type) => {
        event.preventDefault();
        const { value } = this.state;
        const change = value.change();
        const { document } = value;
        // Everything but aligned paragraphs and lists
        if (type !== 'bulleted-list' && type !== 'numbered-list' && type.indexOf('paragraph-') === -1 && type.indexOf('heading-') === -1) {
            const isActive = this.hasBlock(type);
            const isList = this.hasBlock('list-item');

            if (isList) {
                change
                    .setBlocks(isActive ? DEFAULT_NODE : type)
                    .unwrapBlock('bulleted-list')
                    .unwrapBlock('numbered-list');
            } else {
                change.setBlocks(isActive ? DEFAULT_NODE : type);
            }
            // Paragraphs
        } else if (type.indexOf('paragraph-') > -1) {
            const isActive = this.hasBlock(type);

            if (!isActive) {
                change.setBlocks({
                    object: 'block',
                    type: type,
                    data: {
                        // left, center, right or justify
                        className: R_ALIGNED_BLOCK[type.split('-')[1]],
                    },
                });
            } else {
                change.setBlocks(DEFAULT_NODE);
            }
            // h1,2,3,4...
        } else if (type.indexOf('heading-') > -1) {
            const isActive = this.hasBlock(type);

            if (!isActive) {
                change.setBlocks({
                    object: 'block',
                    type: type,
                    data: {
                        // left, center, right or justify
                        className: R_ALIGNED_BLOCK[type.split('-')[2]],
                    },
                });
            } else {
                change.setBlocks(DEFAULT_NODE);
            }
            // Lists
        } else {
            try {
                // Handle the extra wrapping required for list buttons.
                const isList = this.hasBlock('list-item');

                const isType = value.blocks.some((block) => {
                    return !!document.getClosest(block.key, (parent) => parent.type === type);
                });

                if (isList && isType) {
                    change.setBlocks(DEFAULT_NODE).unwrapBlock('bulleted-list').unwrapBlock('numbered-list');
                } else if (isList) {
                    change.unwrapBlock(type === 'bulleted-list' ? 'numbered-list' : 'bulleted-list').wrapBlock(type);
                } else {
                    change.setBlocks('list-item').wrapBlock(type);
                }
            } catch (err) {
                console.log(err);
            }
        }

        this.onChange(change);
    };

    /**
     * Render.
     *
     * @return {Element}
     */

    render() {
        let zIndex = this.props.style && this.props.style.zIndex ? this.props.style.zIndex : undefined;
        return (
            <Popover isOpen={this.state.menuVisible} body={this.renderToolbar()} style={{ zIndex: zIndex }}>
                {this.renderEditor()}
            </Popover>
        );
    }

    /**
     * Render the toolbar.
     *
     * @return {Element}
     */

    renderToolbar = () => {
        if (this.props.simpleMode) {
            return (
                <div className={'pdc-utils-rich-text-toolbar'}>
                    {this.renderMarkButton('bold', 'fa-bold')}
                    {this.renderMarkButton('italic', 'fa-italic')}
                    {this.renderMarkButton('underline', 'fa-underline')}
                </div>
            );
        } else {
            let currentBlock = 'paragraph';

            //isHeading
            if (this.state.value.blocks.some((node) => node.type.indexOf('heading-one') !== -1)) {
                currentBlock = 'heading-one';
            } else if (this.state.value.blocks.some((node) => node.type.indexOf('heading-two') !== -1)) {
                currentBlock = 'heading-two';
            } else if (this.state.value.blocks.some((node) => node.type.indexOf('heading-three') !== -1)) {
                currentBlock = 'heading-three';
            } else if (this.state.value.blocks.some((node) => node.type.indexOf('heading-four') !== -1)) {
                currentBlock = 'heading-four';
            }

            return (
                <div className={'pdc-utils-rich-text-toolbar'}>
                    {this.renderMarkButton('bold', 'fa-bold')}
                    {this.renderMarkButton('italic', 'fa-italic')}
                    {this.renderMarkButton('underline', 'fa-underline')}

                    {this.renderBlockButton(`${currentBlock}-left`, 'fa-align-left')}
                    {this.renderBlockButton(`${currentBlock}-center`, 'fa-align-center')}
                    {this.renderBlockButton(`${currentBlock}-right`, 'fa-align-right')}
                    {this.renderBlockButton(`${currentBlock}-justify`, 'fa-align-justify')}

                    {this.props.noHeading !== true && this.renderBlockButton('heading-one', 'fa-header', '1')}
                    {this.props.noHeading !== true && this.renderBlockButton('heading-two', 'fa-header', '2')}
                    {this.props.noHeading !== true && this.renderBlockButton('heading-three', 'fa-header', '3')}
                    {this.props.noHeading !== true && this.renderBlockButton('heading-four', 'fa-header', '4')}
                    {this.renderBlockButton('block-quote', 'fa-quote-left')}
                    {this.renderBlockButton('numbered-list', 'fa-list-ol')}
                    {this.renderBlockButton('bulleted-list', 'fa-list-ul')}

                    {this.renderLinkButton()}
                </div>
            );
        }
    };

    /**
     * Render a mark-toggling toolbar button.
     *
     * @param {String} type
     * @param {String} icon
     * @return {Element}
     */

    renderMarkButton = (type, icon) => {
        const isActive = this.hasMark(type);
        const onMouseDown = (e) => this.onClickMark(e, type);

        return (
            <span>
                <IconButton
                    name={icon}
                    shape={SHAPES.ROUNDEDSQUARE}
                    primaryColor={toolbarButtonPrimary}
                    secondaryColor={toolbarButtonSecondary}
                    padding={8}
                    inverted={!isActive}
                    onMouseDown={onMouseDown}
                />{' '}
            </span>
        );
    };

    /**
     * Render add link button
     * @return {Element}
     */

    renderLinkButton = () => {
        const isActive = this.hasLink();
        const onMouseDown = (e) => this.onClickLink(e);

        return (
            <span>
                <IconButton
                    name={'md-link'}
                    shape={SHAPES.ROUNDEDSQUARE}
                    primaryColor={toolbarButtonPrimary}
                    secondaryColor={toolbarButtonSecondary}
                    padding={8}
                    inverted={!isActive}
                    onMouseDown={onMouseDown}
                />{' '}
            </span>
        );
    };

    /**
     * Render a block-toggling toolbar button.
     *
     * @param {String} type
     * @param {String} icon
     * @return {Element}
     */

    renderBlockButton = (type, icon, subIndex) => {
        const isActive = this.hasBlock(type);
        const onMouseDown = (e) => this.onClickBlock(e, type);
        return (
            <span style={{ position: 'relative' }}>
                <IconButton
                    name={icon}
                    shape={SHAPES.ROUNDEDSQUARE}
                    primaryColor={toolbarButtonPrimary}
                    secondaryColor={toolbarButtonSecondary}
                    padding={8}
                    inverted={!isActive}
                    onMouseDown={onMouseDown}
                />{' '}
                {subIndex && (
                    <span
                        style={{
                            position: 'absolute',
                            zIndex: 100,
                            color: isActive ? toolbarButtonPrimary : toolbarButtonSecondary,
                            bottom: -8,
                            right: 5,
                            fontSize: '10px',
                        }}
                    >
                        {subIndex}
                    </span>
                )}
            </span>
        );
    };

    /**
     * Placeholder render method override
     *
     * @param {Object} props
     * @return {Element}
     */

    renderPlaceholder = (props) => {
        const { node, editor } = props;
        if (node.object !== 'block') return;
        if (node.type !== 'caption') return;
        if (node.text !== '') return;

        return (
            <span contentEditable={false} style={{ display: 'inline-block', width: '0', whiteSpace: 'nowrap', opacity: '0.42' }}>
                {editor.props.placeholder}
            </span>
        );
    };

    /**
     * Render the Slate editor.
     *
     * @return {Element}
     */

    renderEditor = () => {
        let style = {};
        if (this.props.style) style = this.props.style;
        let className = '';
        if (this.props.className) className += ' ' + this.props.className;
        return (
            <div className={className} style={style} data-id={this.props.slot} data-type="rich-text-editor">
                <Editor
                    renderPlaceholder={this.renderPlaceholder}
                    placeholder={this.props.placeholder}
                    value={this.state.value}
                    onChange={this.onChange}
                    onKeyDown={this.onKeyDown}
                    renderNode={this.renderNode}
                    renderMark={this.renderMark}
                    spellCheck
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    ref={this.editorRef}
                />
            </div>
        );
    };

    /**
     * Render a Slate node.
     *
     * @param {Object} props
     * @return {Element}
     */

    renderNode = (props) => {
        const { attributes, children, node } = props;
        switch (node.type) {
            case 'paragraph':
                return <p {...attributes}>{children}</p>;
            case 'paragraph-left':
            case 'paragraph-right':
            case 'paragraph-center':
            case 'paragraph-justify':
                return (
                    <p className={node.data.get('className')} {...attributes}>
                        {children}
                    </p>
                );
            case 'quote':
                return <blockquote {...attributes}>{children}</blockquote>;
            case 'bulleted-list':
                return <ul {...attributes}>{children}</ul>;
            case 'heading-one':
                return <h1 {...attributes}>{children}</h1>;
            case 'heading-one-left':
            case 'heading-one-right':
            case 'heading-one-center':
            case 'heading-one-justify':
                return (
                    <h1 className={node.data.get('className')} {...attributes}>
                        {children}
                    </h1>
                );
            case 'heading-two':
                return <h2 {...attributes}>{children}</h2>;
            case 'heading-two-left':
            case 'heading-two-right':
            case 'heading-two-center':
            case 'heading-two-justify':
                return (
                    <h2 className={node.data.get('className')} {...attributes}>
                        {children}
                    </h2>
                );
            case 'heading-three':
                return <h3 {...attributes}>{children}</h3>;
            case 'heading-three-left':
            case 'heading-three-right':
            case 'heading-three-center':
            case 'heading-three-justify':
                return (
                    <h3 className={node.data.get('className')} {...attributes}>
                        {children}
                    </h3>
                );
            case 'heading-four':
                return <h4 {...attributes}>{children}</h4>;
            case 'heading-four-left':
            case 'heading-four-right':
            case 'heading-four-center':
            case 'heading-four-justify':
                return (
                    <h4 className={node.data.get('className')} {...attributes}>
                        {children}
                    </h4>
                );
            case 'list-item':
                return <li {...attributes}>{children}</li>;
            case 'numbered-list':
                return <ol {...attributes}>{children}</ol>;
            case 'link': {
                const { data } = node;
                const href = data.get('href');
                return (
                    <a href={href} {...attributes} target={href.startsWith('#') ? undefined : '_blank'} rel="noopener noreferrer">
                        {children}
                    </a>
                );
            }
            default:
                return <p>{children}</p>;
            /*case 'image': {
                const src = node.data.get('src');
                const className = isSelected ? 'active' : null;
                const style = { display: 'block' };
                return (
                    <img
                        src={src}
                        className={className}
                        style={style}
                        {...attributes}
                    />
                );
            }*/
        }
    };

    /**
     * Render a Slate mark.
     *
     * @param {Object} props
     * @return {Element}
     */

    renderMark = (props) => {
        const { children, mark } = props;
        switch (mark.type) {
            case 'bold':
                return <strong>{children}</strong>;
            case 'italic':
                return <em>{children}</em>;
            case 'underline':
                return <u>{children}</u>;
            case 'link':
                return (
                    <a
                        href={mark.data.get('href')}
                        target={mark.data.get('href').startsWith('#') ? undefined : '_blank'}
                        rel="noopener noreferrer"
                    >
                        {children}
                    </a>
                );
            default:
                return <em>{children}</em>;
        }
    };
}
